import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Alert,
} from "@mui/material";
import dayjs from "../../shared utils/dayjsConfig.js";

const fmt = (d) => (d ? dayjs(d).format("ddd D MMM") : "");

function roomLabel(room) {
  if (!room) return "the room";
  const house = room.house ? `${room.house} ` : "";
  return `${house}${room.bedroomName || "room"}`;
}

// Shown when a save is rejected because the room would be over capacity
// (backend 409, code CAPACITY_CONFLICT). Offers to take the room for as long as
// it's actually free, or to revert the placement. `conflict` is null when
// closed.
export default function CapacityConflictDialog({
  conflict,
  onClamp,
  onRevert,
  onDismiss,
}) {
  if (!conflict) return null;

  const { resident, room, intendedStart, freeThrough, canClamp, firstUnavailableDate } =
    conflict;
  const name =
    `${resident?.name || ""} ${resident?.surname || ""}`.trim() || "this resident";
  const label = roomLabel(room);

  return (
    <Dialog open onClose={onDismiss} maxWidth="sm" fullWidth>
      <DialogTitle>Room over capacity</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">
          <strong>{label}</strong> is already full from{" "}
          <strong>{fmt(firstUnavailableDate)}</strong> — it can&apos;t take {name}{" "}
          for their whole stay.
        </DialogContentText>

        <Alert severity={canClamp ? "info" : "warning"} sx={{ mt: 2 }}>
          {canClamp ? (
            <>
              {label} is free from <strong>{fmt(intendedStart)}</strong> to{" "}
              <strong>{fmt(freeThrough)}</strong>. You can assign it for those
              nights and place {name} elsewhere for the rest.
            </>
          ) : (
            <>
              {label} isn&apos;t free from <strong>{fmt(intendedStart)}</strong>,
              so there are no nights to assign here. Revert this placement and pick
              another room.
            </>
          )}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onDismiss} color="inherit">
          Keep editing
        </Button>
        <Button onClick={onRevert}>Revert</Button>
        {canClamp && (
          <Button onClick={onClamp} variant="contained">
            Assign {fmt(intendedStart)}–{fmt(freeThrough)}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
